import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import React from "react";
import { m } from "framer-motion";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { ScrollFeaturesFeatureView } from "~/features/blocks/inventory/scroll-features/scroll-features-feature";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type ScrollFeaturesBlockViewItem = {
  key: React.Key;
  media?: React.ReactNode;
  hasMedia?: boolean;
  hasOverlay?: boolean;
  headline?: string | null;
};
export type ScrollFeaturesBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  features?: ScrollFeaturesBlockViewItem[] | null;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function ScrollFeaturesBlockView({
  anchor,
  features = [],
  hasTopDivider = true,
  mode = "light",
  sectionProps,
  theme = "white"
}: ScrollFeaturesBlockViewProps) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const entries = features ?? [];
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [showMedia, setShowMedia] = React.useState(false);
  if (!hasItems(entries)) {
    return null;
  }
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.border, themeStyle.background, themeStyle.text]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2>
            <_StyledMDiv>
              <_StyledDiv3>
                {entries.map((feature, index) => {
                const isActive = activeIndex === index;
                if (!feature.media) {
                  return null;
                }
                return <_StyledMDiv2 key={index} initial={{
                  opacity: 0
                }} animate={{
                  opacity: isActive ? 1 : 0
                }} $_css2={[{
                  "height": "100%",
                  "width": "100%",
                  "opacity": "0"
                }, feature.hasOverlay && {
                  "&::before": {
                    "content": "var(--tw-content)",
                    "position": "absolute",
                    "inset": "0",
                    "zIndex": "10",
                    "backgroundColor": "rgba(0, 7, 4, 0.4)",
                    "--tw-content": "\"\""
                  }
                }]}>
                      {feature.media}
                    </_StyledMDiv2>;
              })}
              </_StyledDiv3>
            </_StyledMDiv>
          </_StyledDiv2>
          <_StyledDiv4 $_css3={[{
          "overflow": "hidden",
          "paddingTop": "15vh",
          "paddingBottom": "15vh",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.border]}>
            <_StyledUl>
              {entries.map((feature, index) => <_StyledLi key={feature.key ?? index}>
                  <ScrollFeaturesFeatureView index={index} setActive={setActiveIndex} active={activeIndex === index} setShowMedia={setShowMedia} showMedia={showMedia} hasMedia={Boolean(feature.hasMedia)} headline={feature.headline} />
                </_StyledLi>)}
            </_StyledUl>
          </_StyledDiv4>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-18yrfbx-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-18yrfbx-1"
})({
  "position": "relative",
  "display": "flex",
  "width": "100%",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-18yrfbx-2"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledMDiv = _styled4(m.div).withConfig({
  componentId: "sc-sf-18yrfbx-3"
})({
  "pointerEvents": "none",
  "position": "sticky",
  "top": "var(--sub-nav-height)",
  "zIndex": "10",
  "height": "calc(100vh - var(--sub-nav-height))",
  "overflow": "hidden",
  "padding": "2rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "6rem",
    "paddingRight": "6rem"
  }
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-18yrfbx-4"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledMDiv2 = _styled6(m.div).withConfig({
  componentId: "sc-sf-18yrfbx-5"
})`${p => p.$_css2}`;
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-18yrfbx-6"
})`${p => p.$_css3}`;
var _StyledUl = _styled8("ul").withConfig({
  componentId: "sc-sf-18yrfbx-7"
})({
  "marginBottom": "-50vh",
  "marginTop": "50vh",
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "center",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "paddingTop": "16rem",
  "paddingBottom": "16rem",
  "textAlign": "center"
});
var _StyledLi = /*#__PURE__*/_styled9("li").withConfig({
  displayName: "scroll-features___StyledLi",
  componentId: "sc-1cvsnji-0"
})({
  "maxWidth": "120rem"
});